import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { checkEmployeeData, updateEmployeePhone } from '../utils/checkEmployee';

const EmployeeDiagnostic = ({ onSwitchToEmployee }) => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [diagnosticResult, setDiagnosticResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phoneInput, setPhoneInput] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('payroll_summary')
        .select('employee_number, employee_name')
        .order('employee_name', { ascending: true });

      if (error) throw error;

      // Get unique employees
      const uniqueEmployees = Array.from(
        new Map(data.map(item => [item.employee_number, item])).values()
      );

      setEmployees(uniqueEmployees);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const runDiagnostic = async () => {
    if (!selectedEmployee) return;
    
    setLoading(true);
    const result = await checkEmployeeData(selectedEmployee);
    setDiagnosticResult(result);
    setLoading(false);
  };

  const handleEmployeeChange = (e) => {
    setSelectedEmployee(e.target.value);
    setDiagnosticResult(null);
    setPhoneInput('');
  };

  const handleUpdatePhone = async () => {
    if (!phoneInput.trim()) {
      alert('Please enter a phone number');
      return;
    }

    setUpdating(true);
    const result = await updateEmployeePhone(selectedEmployee, phoneInput);
    
    if (result.success) {
      alert(result.message);
      await runDiagnostic(); // Refresh the diagnostic
      setPhoneInput('');
    } else {
      alert('Error updating phone: ' + result.error);
    }
    
    setUpdating(false);
  };

  const handleSwitchToEmployeeView = async () => {
    if (!selectedEmployee) {
      alert('Please select an employee first');
      return;
    }

    if (window.confirm(`Switch to ${diagnosticResult.employee.name}'s view?`)) {
      // Pass the employee data to the parent component
      if (onSwitchToEmployee) {
        onSwitchToEmployee({
          employeeNumber: diagnosticResult.employee.number,
          employeeName: diagnosticResult.employee.name,
          employeePhone: diagnosticResult.employee.phone
        });
      }
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">Employee Login Diagnostic Tool</h2>
      
      {/* Employee Selector */}
      <div className="bg-white shadow rounded-lg p-4 mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Employee:
        </label>
        <div className="flex gap-2">
          <select
            value={selectedEmployee}
            onChange={handleEmployeeChange}
            className="flex-1 p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
            disabled={loading}
          >
            <option value="">-- Select an employee --</option>
            {employees.map((emp) => (
              <option key={emp.employee_number} value={emp.employee_number}>
                {emp.employee_name} ({emp.employee_number})
              </option>
            ))}
          </select>
          <button
            onClick={runDiagnostic}
            disabled={!selectedEmployee || loading}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            Run Diagnostic
          </button>
        </div>
      </div>

      {/* Diagnostic Results */}
      {diagnosticResult && (
        <>
          {!diagnosticResult.success && diagnosticResult.error.includes('not found') ? (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              <p className="font-bold">Employee Not Found</p>
              <p>{diagnosticResult.solution}</p>
            </div>
          ) : (
            <>
              <div className="bg-white shadow rounded-lg p-4 mb-4">
                <h3 className="font-semibold mb-2">Employee Information</h3>
                <div className="grid grid-cols-2 gap-2">
                  <div>Name:</div>
                  <div>{diagnosticResult.employee?.name || 'N/A'}</div>
                  <div>Employee Number:</div>
                  <div>{diagnosticResult.employee?.number || 'N/A'}</div>
                  <div>Phone:</div>
                  <div className={diagnosticResult.employee?.phone ? 'text-green-600' : 'text-red-600'}>
                    {diagnosticResult.employee?.phone || 'NOT SET'}
                  </div>
                  <div>Email:</div>
                  <div>{diagnosticResult.employee?.email || 'NOT SET'}</div>
                </div>

                {/* Switch to Employee View Button */}
                <button
                  onClick={handleSwitchToEmployeeView}
                  className="mt-4 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Switch to {diagnosticResult.employee.name}'s View
                </button>
              </div>

              {diagnosticResult.issues && diagnosticResult.issues.length > 0 && (
                <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
                  <p className="font-bold mb-2">Issues Found:</p>
                  <ul className="list-disc list-inside">
                    {diagnosticResult.issues.map((issue, index) => (
                      <li key={index}>{issue}</li>
                    ))}
                  </ul>
                  
                  {diagnosticResult.solutions && diagnosticResult.solutions.length > 0 && (
                    <>
                      <p className="font-bold mt-4 mb-2">Solutions:</p>
                      <ul className="list-disc list-inside">
                        {diagnosticResult.solutions.map((solution, index) => (
                          <li key={index}>{solution}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}

              {diagnosticResult.duplicates && diagnosticResult.duplicates.length > 0 && (
                <div className="bg-orange-100 border border-orange-400 text-orange-700 px-4 py-3 rounded mb-4">
                  <p className="font-bold">Warning: Phone number is shared with:</p>
                  <ul className="list-disc list-inside">
                    {diagnosticResult.duplicates.map((dup, index) => (
                      <li key={index}>{dup.employee_name} ({dup.employee_number})</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded">
                <p className="font-bold mb-2">Update Phone Number</p>
                <div className="flex gap-2">
                  <input
                    type="tel"
                    value={phoneInput}
                    onChange={(e) => setPhoneInput(e.target.value)}
                    placeholder="Enter phone number (e.g., 1234567890)"
                    className="flex-1 p-2 border rounded"
                    disabled={updating}
                  />
                  <button
                    onClick={handleUpdatePhone}
                    disabled={updating || !selectedEmployee}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {updating ? 'Updating...' : 'Update Phone'}
                  </button>
                </div>
                <p className="text-sm mt-2">Enter 10 or 11 digit phone number</p>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default EmployeeDiagnostic;